// BoardLane (round 2): one technician's lane on the board (DESIGN.md §8). On the left, held still while
// the track scrolls: their pen and name, the line that says what's happening with them (off today, not
// clocked in, on the clock since when and where, on a non-job event, clocked out with visits left: BL-158
// 2), which opens their timesheet, and their day (visits, hours, driving) or, when something's wrong, that
// in red. On the right, the track: their visits placed by time, stacked when they overlap, the drive
// between them (red when it doesn't fit), time off hatched, and the over-run of a visit running over.
// Every lane is the same height whatever its words: 80 for one row, 68 for each extra stack; 36 compact.
// Right-click the head, or press the name, for the technician's menu (BL-158 3): the lane says where;
// the menu is the shared Menu, composed by the screen. While a visit is picked up to give (tap-to-give),
// the whole lane is the target: a tap on the track, or on the name and the lines under it, gives it to
// this technician (QA-387: on a phone the names stay in view over the scrolled track, so a finger lands
// there), and neither the menu nor the timesheet opens. The time away is drawn on the track and said
// once, in the lane's label. The No one yet lane is the same drawing with nobody's pen.
// While the board's divisions are being arranged (concept A, 26 Oct), ⋮⋮ before the name drags the lane
// to another division (the screen does the drag), and by keyboard it opens a menu of where to. A
// technician moved to another division for the day has a dashed pen key, and their day's line says where
// they usually are ("Usually in Service"), in ink. Reads only --lane-*.
import type { ButtonHTMLAttributes, KeyboardEvent, MouseEvent, PointerEvent } from 'react';
import { Glyph } from '../Glyph/Glyph.tsx';
import { spanHours, spanPos, useTimeSpan } from '../time-span.tsx';
import { HoverCard } from '../HoverCard/HoverCard.tsx';
import type { KeyValueItem } from '../KeyValue/KeyValue.tsx';
import type { MenuAt } from '../Menu/Menu.tsx';
import { NowLine } from '../NowLine/NowLine.tsx';
import type { Pen } from '../pen.ts';
import { PenKey } from '../PenKey/PenKey.tsx';
import { VisitBlock, type VisitBlockProps } from '../VisitBlock/VisitBlock.tsx';
import './BoardLane.css';

export interface LaneVisit extends Omit<VisitBlockProps, 'density' | 'selected' | 'pen' | 'ref'> {
  id: string;
  /** Its booked start and end, in minutes from midnight. */
  start: number;
  end: number;
  /** Running over: the over-run is drawn from its end to here (now). */
  overUntil?: number;
  /** The drive from the visit before it: its usual minutes, and whether the gap is shorter. */
  driveBefore?: { minutes: number; short?: boolean };
  /** What the hover card shows: only what the block doesn't. A problem (`late`) is put first. */
  hover?: KeyValueItem[];
}

export interface LaneOff {
  start: number;
  end: number;
  /** "Off from 4 p.m.", "Dentist", "Team meeting". */
  label: string;
}

export interface LaneLine {
  words: string;
  /** A problem, in red: running late, clocked out with visits left. */
  bad?: boolean;
}

/** ⋮⋮ before the name, while arranging: a drag handle, and by keyboard a menu of where to. */
export interface LaneGrip {
  /** "Move Sam Rivera to another division". */
  label: string;
  onMenu: (at: MenuAt) => void;
  /** A press on it: the screen's drag starts here. */
  onPointerDown?: (e: PointerEvent<HTMLButtonElement>) => void;
  /** Its menu is open. */
  isOpen?: boolean;
}

/** Where the technician's menu was asked for: their name (a right-click, a press, the keyboard). */
export type TechMenuAt = MenuAt;

export interface BoardLaneProps {
  /** A technician's lane, or the lane of visits nobody has yet. */
  kind?: 'tech' | 'unassigned';
  name: string;
  /**
   * The name a phone's narrow names column shows ("Alex M.", QA-765); the full name stays in the
   * lane's label and the menu's. Without it, the name.
   */
  shortName?: string;
  pen?: Pen | null;
  /** What's happening with them now; pressing it opens their timesheet when onTimesheet is given. */
  detail?: LaneLine | null;
  /** Their day ("4 visits, 6 h, 35 min driving"), or what's wrong with it, in red. */
  day?: LaneLine | null;
  /**
   * A problem with their time on an earlier day, in red under the line above ("No clock-out Oct 23",
   * problemLaneWords). The head stays three lines, so it takes the place of `day`.
   */
  problem?: string | null;
  /** Their id (staff id): the name carries it (data-lane-name) so focus can come back to it. */
  laneId?: string;
  /**
   * Their time away today in words ("Training, 1–3 p.m."), for the lane's label: the track draws it
   * (`off`) hidden from screen readers, so it is said once, here.
   */
  away?: readonly string[];
  visits: LaneVisit[];
  off?: LaneOff[];
  selectedId?: string | null;
  onSelectVisit?: (id: string) => void;
  /** A double press: straight to the visit's job. */
  onOpenVisit?: (id: string) => void;
  /**
   * Opens their timesheet at today. Pass it only to people who may open timesheets (owner, manager,
   * bookkeeper); without it the detail line is plain words, not a button.
   */
  onTimesheet?: () => void;
  onTechMenu?: (at: TechMenuAt) => void;
  /** A visit being dragged would land here. */
  isTarget?: boolean;
  /** Anything more a visit's button needs (phase B's drag: pointer and key handlers). */
  visitProps?: (v: LaneVisit) => ButtonHTMLAttributes<HTMLButtonElement>;
  /**
   * A drag's ghost: where the visit being moved would land, drawn over the first row, never stacked
   * (its `drag` is 'ghost' or 'ghost-bad'). Hidden from screen readers: the page says the move in words.
   */
  ghost?: LaneVisit | null;
  /**
   * Tap-to-give: a visit is picked up, and a press anywhere on the track gives it to this lane. One
   * button over the track, beside the visits rather than around them, so no control holds another; it
   * is the keyboard's and the screen reader's target. A tap on the head gives it too (QA-387), and the
   * name and the line under it are words while it lasts, not the menu and the timesheet.
   */
  pick?: { label: string; onPick: () => void } | null;
  /** Arranging: ⋮⋮ before the name. */
  grip?: LaneGrip | null;
  /** In another division for the day: a dashed pen key, and `day` (where they usually are) in ink. */
  moved?: boolean;
  /** Being dragged: faded where it was. */
  lifted?: boolean;
}

/** Where a visit's drawing ends: its booked end, or the end of its over-run. */
const reach = (v: { end: number; overUntil?: number }) => Math.max(v.end, v.overUntil ?? 0);

/** The visits in rows: each goes in the first row it doesn't overlap (over-runs too), in order of start. */
export function laneStacks<T extends { start: number; end: number; overUntil?: number }>(
  visits: readonly T[],
): T[][] {
  const rows: T[][] = [];
  for (const v of [...visits].sort((a, b) => a.start - b.start || b.end - a.end)) {
    const row = rows.find((r) => reach(r[r.length - 1]!) <= v.start);
    if (row) row.push(v);
    else rows.push([v]);
  }
  return rows.length ? rows : [[]];
}

const pos = spanPos;

/**
 * Puts focus back on a lane's name (its menu's button), after a dialog opened from its menu closes. It
 * waits two frames, so it lands after the dialog hands focus back to wherever it came from.
 */
export function focusLaneName(laneId: string): void {
  const go = () => document.querySelector<HTMLElement>(`[data-lane-name="${CSS.escape(laneId)}"]`)?.focus();
  requestAnimationFrame(() => requestAnimationFrame(go));
}

/** A gap this long or more is drawn as a drive, and one this long has room for its minutes. */
const PIPE_MIN = 15;
const PIPE_WORDS_MIN = 34;
/** A visit this short or shorter gives up its padding so its start shows. */
const NARROW_MIN = 20;

export function BoardLane({
  kind = 'tech',
  name,
  shortName,
  pen,
  detail,
  day,
  problem,
  laneId,
  away = [],
  visits,
  off = [],
  selectedId,
  onSelectVisit,
  onOpenVisit,
  onTimesheet,
  onTechMenu,
  isTarget,
  visitProps,
  ghost,
  pick,
  grip,
  moved,
  lifted,
}: BoardLaneProps) {
  const span = useTimeSpan();
  const compact = span.density === 'compact';
  const hours = spanHours(span).length - 1 || 1;
  const rows = laneStacks(visits);
  const nobody = kind === 'unassigned';

  // while a visit is picked up, the head gives it to this lane: no menu, no timesheet
  const menu = pick ? undefined : onTechMenu;
  const timesheet = pick ? undefined : onTimesheet;

  const contextMenu = (e: MouseEvent<HTMLElement>) => {
    if (!menu || nobody) return;
    e.preventDefault();
    // a right-click opens it beside the name, as the keyboard does, wherever on the head it landed
    const btn = e.currentTarget.querySelector<HTMLElement>('.fl-lane__name');
    if (btn) menu({ element: btn });
    else menu({ x: e.clientX, y: e.clientY });
  };

  // the keyboard's way: the menu key or Shift+F10 on the name (or the line under it), opening at the name
  const menuKey = (e: KeyboardEvent<HTMLElement>) => {
    if (!menu || nobody) return;
    if (e.key !== 'ContextMenu' && !(e.shiftKey && e.key === 'F10')) return;
    const btn = e.currentTarget.querySelector<HTMLElement>('.fl-lane__name');
    if (!btn) return;
    e.preventDefault();
    menu({ element: btn });
  };

  // a phone draws the short name in its narrow column (QA-765); either way the label says it in full
  const words =
    shortName && shortName !== name ? (
      <>
        <span className="fl-lane__full">{name}</span>
        <span className="fl-lane__short">{shortName}</span>
      </>
    ) : (
      name
    );
  const nameFace = nobody ? (
    words
  ) : (
    <PenKey pen={pen} dashed={moved}>
      {words}
    </PenKey>
  );
  const nameButton =
    menu && !nobody ? (
      <button
        type="button"
        className="fl-lane__name"
        data-lane-name={laneId}
        aria-haspopup="menu"
        aria-label={`${name}: their menu`}
        onClick={(e) => menu({ element: e.currentTarget })}
      >
        {nameFace}
      </button>
    ) : (
      <span className="fl-lane__name">{nameFace}</span>
    );
  const lines = [detail?.words, problem || day?.words, away.join('; ')].filter(Boolean).join('. ');
  return (
    <div
      className="fl-lane"
      role="group"
      // one period at the end, even after "p.m."
      aria-label={lines ? `${name}. ${lines}${lines.endsWith('.') ? '' : '.'}` : name}
      data-kind={kind}
      data-density={span.density}
      data-target={isTarget ? '' : undefined}
      data-picking={pick ? '' : undefined}
      data-moved={moved ? '' : undefined}
      data-gripped={grip ? '' : undefined}
      data-lifted={lifted ? '' : undefined}
    >
      {/* a tap on the head gives the picked visit (a pointer's way: the track's button is the keyboard's) */}
      <div
        className="fl-lane__head"
        onContextMenu={contextMenu}
        onKeyDown={menuKey}
        onClick={pick ? pick.onPick : undefined}
      >
        {grip ? (
          <span className="fl-lane__nameline">
            <button
              type="button"
              className="fl-lane__grip"
              data-lane-grip={laneId}
              aria-label={grip.label}
              aria-haspopup="menu"
              aria-expanded={grip.isOpen ?? false}
              onPointerDown={grip.onPointerDown}
              onClick={(e) => grip.onMenu({ element: e.currentTarget })}
            >
              <Glyph name="grip" className="fl-lane__grip-glyph" />
            </button>
            {nameButton}
          </span>
        ) : (
          nameButton
        )}
        {!compact && detail ? (
          timesheet ? (
            <button
              type="button"
              className="fl-lane__line fl-lane__detail"
              data-bad={detail.bad ? '' : undefined}
              aria-label={`${detail.words}. Open ${name}’s timesheet for today.`}
              onClick={timesheet}
            >
              {detail.words}
            </button>
          ) : (
            <span className="fl-lane__line" data-bad={detail.bad ? '' : undefined}>
              {detail.words}
            </span>
          )
        ) : null}
        {!compact && problem ? (
          <span className="fl-lane__line" data-bad="">
            {problem}
          </span>
        ) : !compact && day ? (
          <span
            className="fl-lane__line"
            data-bad={day.bad ? '' : undefined}
            data-usually={moved && !day.bad ? '' : undefined}
          >
            {day.words}
          </span>
        ) : null}
      </div>
      <div
        className="fl-lane__track"
        style={{ backgroundSize: `${100 / hours}% 100%, ${100 / hours / 4}% 100%` }}
      >
        {off.map((o) => (
          // drawn only: the lane's label says the time away (`away`), so a screen reader hears it once
          <div
            key={`${o.start}-${o.label}`}
            className="fl-lane__off"
            style={pos(span, o.start, o.end)}
            aria-hidden="true"
          >
            <span>{o.label}</span>
          </div>
        ))}
        {rows.map((row, r) => (
          <div key={r} className="fl-lane__row">
            {row.map((v, i) => {
              const prev = row[i - 1];
              // nobody drives the No one yet lane: no drives drawn there
              const from = prev && !nobody ? reach(prev) : null;
              const gap = from === null ? 0 : v.start - from;
              const pipe =
                from !== null && (gap >= PIPE_MIN || (v.driveBefore?.short && gap > 0)) ? (
                  <span
                    key={`p${v.id}`}
                    className="fl-lane__pipe"
                    data-bad={v.driveBefore?.short ? '' : undefined}
                    style={pos(span, from, v.start)}
                    aria-hidden="true"
                  >
                    <span className="fl-lane__pipe-line" />
                    {v.driveBefore && gap >= PIPE_WORDS_MIN ? (
                      <span className="fl-lane__pipe-words">{v.driveBefore.minutes} min</span>
                    ) : null}
                  </span>
                ) : null;
              const over =
                v.overUntil && v.overUntil > v.end ? (
                  <span
                    key={`o${v.id}`}
                    className="fl-lane__over"
                    style={pos(span, v.end, v.overUntil)}
                    aria-hidden="true"
                  />
                ) : null;
              const { id, start: _s, end: _e, overUntil: _o, driveBefore: _d, hover: more, ...block } = v;
              // a problem leads the card: a narrow block draws it without its words (VisitBlock)
              const hover = block.late
                ? [{ label: 'Problem', value: block.late, bad: true }, ...(more ?? [])]
                : more;
              const extra = visitProps?.(v) ?? {};
              const blockEl = (
                <VisitBlock
                  {...block}
                  {...extra}
                  pen={nobody ? null : pen}
                  state={nobody ? 'unassigned' : v.state}
                  density={span.density}
                  narrow={v.end - v.start <= NARROW_MIN}
                  selected={selectedId === id}
                  onClick={(e) => {
                    extra.onClick?.(e);
                    onSelectVisit?.(id);
                  }}
                  onDoubleClick={(e) => {
                    extra.onDoubleClick?.(e);
                    onOpenVisit?.(id);
                  }}
                />
              );
              return [
                pipe,
                over,
                <span key={id} className="fl-lane__slot" style={pos(span, v.start, v.end)}>
                  {hover?.length ? (
                    <HoverCard label={`More about ${v.customer}`} fields={hover}>
                      {blockEl}
                    </HoverCard>
                  ) : (
                    blockEl
                  )}
                </span>,
              ];
            })}
          </div>
        ))}
        {ghost ? (
          <div className="fl-lane__ghost" aria-hidden="true">
            <span className="fl-lane__slot" style={pos(span, ghost.start, ghost.end)}>
              <VisitBlock
                state={ghost.state}
                pen={nobody ? null : pen}
                time={ghost.time}
                timeShort={ghost.timeShort}
                customer={ghost.customer}
                job={ghost.job}
                late={ghost.late}
                label={ghost.label}
                drag={ghost.drag ?? 'ghost'}
                density={span.density}
                narrow={ghost.end - ghost.start <= NARROW_MIN}
                tabIndex={-1}
              />
            </span>
          </div>
        ) : null}
        {pick ? (
          <button type="button" className="fl-lane__pick" aria-label={pick.label} onClick={pick.onPick} />
        ) : null}
        <NowLine />
      </div>
    </div>
  );
}
